import clsx from 'clsx'

import type { TabKey } from './nav'

// ============================================================================
// THE FIVE TAB ICONS AND THEIR ONE-SHOT ANIMATIONS — board 27 (TabIcons.dc.html), whose paths and
// part classes are also in boards 04 (Bar) and 01 (Main). Each glyph is split into the parts that
// move separately (the sun's rays and its core, the bell and its clapper …), every path carries
// `pathLength="1"` so the Notes lines can draw with a dash of exactly 1, and the keyframes live in
// index.css under `[data-shell='mobile']`.
//
// IT PLAYS ONCE, WHEN THE TAB BECOMES ACTIVE — including the tab the app lands on, which the board
// asks for. Not on a re-tap (that scrolls the page to the top instead), and not while scrubbing
// across it. The class is added when the tab becomes active and left on while it stays active; an
// animation runs when its class arrives, so leaving it on is what "once" means.
// Reduced motion: index.css cancels every animation; only the colour changes.
//
// The stroke is `currentColor`: the tab's text colour (inactive grey, or white) unless the icon is
// the active one, which wears the accent through a `text-` class.
// ============================================================================

type Part = readonly [d: string, cls: string]

export const TAB_ICON_PARTS: Record<TabKey, readonly Part[]> = {
  day: [
    ['M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4', 'm-ic-rays'],
    ['M12 8a4 4 0 1 0 0 8a4 4 0 1 0 0-8Z', 'm-ic-core'],
  ],
  school: [
    ['M22 10 12 5 2 10l10 5 10-5Z', 'm-ic-cap'],
    ['M6 12v5c3 2 9 2 12 0v-5', 'm-ic-cap'],
  ],
  chat: [
    ['M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.6A8 8 0 1 1 21 12Z', 'm-ic-chat'],
    ['M8.5 12h.01', 'm-ic-dot'],
    ['M12 12h.01', 'm-ic-dot m-ic-dot2'],
    ['M15.5 12h.01', 'm-ic-dot m-ic-dot3'],
  ],
  reminders: [
    ['M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9', 'm-ic-bell'],
    ['M10.3 21a1.94 1.94 0 0 0 3.4 0', 'm-ic-clap'],
  ],
  notes: [
    ['M5 4h11l3 3v13H5z', 'm-ic-page'],
    ['M9 10h6', 'm-ic-line'],
    ['M9 14h6', 'm-ic-line m-ic-line2'],
  ],
}

export function TabIcon({
  tab,
  play,
  accent,
  lifted,
}: {
  tab: TabKey
  /** Carry the one-shot animation class. */
  play: boolean
  /** Stroke in the accent (the shown tab). */
  accent: boolean
  /** Lifted under the scrubbing finger. */
  lifted: boolean
}) {
  return (
    <svg
      className={clsx('m-ic', play && 'm-play', accent && 'text-m-accent', lifted && 'm-ic-lift')}
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      data-play={play ? 'true' : undefined}
    >
      {TAB_ICON_PARTS[tab].map(([d, cls], i) => (
        <path key={i} className={cls} d={d} pathLength={1} />
      ))}
    </svg>
  )
}

// ── The capture menu's four glyphs (board 03, ITEMS) and the + ──────────────────────────────

export const MENU_ICON_PATHS = {
  voice: 'M9 6a3 3 0 0 1 6 0v5a3 3 0 0 1-6 0Z M5 11a7 7 0 0 0 14 0M12 18v3',
  reminder: 'M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9 M10.3 21a1.94 1.94 0 0 0 3.4 0',
  note: 'M12 20h9 M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z',
  ask: 'M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.6A8 8 0 1 1 21 12Z',
} as const

export function MenuGlyph({ d }: { d: string }) {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.1"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d={d} />
    </svg>
  )
}

export function PlusGlyph() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.6"
      strokeLinecap="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M12 5v14M5 12h14" />
    </svg>
  )
}
